/*
 * @Description: Testimonials section - What Users Say About AI Video Editor
 */
import React from 'react';

interface Testimonial {
  title: string;
  content: string;
  role: string;
  name: string;
}

const testimonials: Testimonial[] = [
  {
    title: 'Finally, Editing Doesn\u2019t Eat My Whole Day',
    content:
      'I used to spend hours cutting clips and fixing small mistakes in my product videos. It slowed down my posting schedule and made editing feel like a chore. FalcoCut AI editor lets me clean up scenes and adjust the pacing of my videos with simple prompts. Now I finish edits in minutes instead of nights.',
    role: 'YouTube Content Creator',
    name: 'Olivia Bennett',
  },
  {
    title: 'Perfect for Quick Client Turnarounds',
    content:
      'As a freelance video editor, I often get last\u2011minute requests that used to stress me out. FalcoCut helps me handle fast edits, subtitle fixes, and translations without opening multiple apps. The text\u2011based editing is a lifesaver when I need to make changes on the fly. My clients think I\u2019m working magic.',
    role: 'Freelance Video Editor',
    name: 'Marcus Holloway',
  },
  {
    title: 'Made My Social Videos Look Way More Professional',
    content:
      'I run a small online shop and always struggled with editing videos for TikTok and Reels. I didn\u2019t know how to cut clips or add effects without messing something up. FalcoCut makes everything simple \u2014 I just type what I want, and the AI handles the rest. My videos look cleaner, sharper, and way more engaging.',
    role: 'Small Business Owner',
    name: 'Jenna Clarke',
  },
  {
    title: 'Great for Recasting Videos Instantly',
    content:
      'We often need to swap talent for different campaigns, and FalcoCut makes it effortless. Upload a photo, give it a prompt, and the character changes while the motion and lighting stay perfectly natural. It saves us from reshoots and keeps our content adaptable across markets.',
    role: 'Creative Director',
    name: 'Daniel Whitmore',
  },
  {
    title: 'Reliable Tool for Our Training Video Edits',
    content:
      'FalcoCut helps us polish short training clips quickly with simple text\u2011based edits. The results stay clean and consistent, which makes it easier to deliver clear learning materials across the company. It\u2019s streamlined our workflow and reduced the time we spend on routine edits.',
    role: 'Learning & Development Specialist',
    name: 'Sarah Lawson',
  },
  {
    title: 'A Huge Upgrade for My Ad Production Workflow',
    content:
      'Creating ad variations used to take forever, especially when clients wanted multiple versions for different markets. FalcoCut lets me edit videos fast, so I can deliver more options without burning out. The AI handles the heavy lifting, and I focus on the creative decisions. It\u2019s changed how I work.',
    role: 'Paid Media Manager',
    name: 'Hailey Wilson',
  },
];

const StarRating: React.FC = () => (
  <div className="flex gap-1 mb-3 pad:mb-4">
    {[1, 2, 3, 4, 5].map((star) => (
      <svg
        key={star}
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 1L12.39 6.26L18 7.27L14 11.14L14.76 17L10 14.27L5.24 17L6 11.14L2 7.27L7.61 6.26L10 1Z"
          fill="#FBBF24"
          stroke="#FBBF24"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ))}
  </div>
);

const TestimonialCard: React.FC<Testimonial> = ({ title, content, role, name }) => {
  return (
    <div className="bg-white rounded-2xl p-6 pad:p-8 h-full flex flex-col">
      <StarRating />
      <h3 className="text-lg pad:text-xl font-bold text-gray-900 mb-3 pad:mb-4">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-gray-600 leading-relaxed flex-grow mb-4 pad:mb-6">
        {content}
      </p>
      <div>
        <p className="font-semibold text-gray-900 text-sm pad:text-base">{role}</p>
        <p className="text-gray-500 text-sm">{name}</p>
      </div>
    </div>
  );
};

const TestimonialsSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-10 pad:mb-14 sm:mb-16 leading-tight">
          What Our Customers Say
        </h2>

        <div className="grid grid-cols-1 pad:grid-cols-2 sm:grid-cols-3 gap-4 pad:gap-6">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              title={testimonial.title}
              content={testimonial.content}
              role={testimonial.role}
              name={testimonial.name}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
